import type { JSX } from "preact";
import { IcoSpinner } from "../icons";

type ButtonVariant = "primary" | "secondary" | "ghost" | "danger";
type ButtonSize = "sm" | "md" | "lg";

type ButtonProps = JSX.IntrinsicElements["button"] & {
  variant?: ButtonVariant;
  size?: ButtonSize;
  loading?: boolean;
};

const variantStyles: Record<ButtonVariant, string> = {
  // Primary: Brand color background, white text
  primary:
    "bg-[var(--brand-600)] text-white hover:bg-[var(--brand-700)] active:bg-[var(--brand-800)]",
  // Secondary: White/surface background with border, neutral text
  secondary:
    "bg-surface border border-border text-text hover:bg-surface-hover active:bg-surface-active",
  // Ghost: Transparent, just text
  ghost: "text-text-secondary hover:bg-surface-hover active:bg-surface-active",
  // Danger: Red for destructive actions
  danger: "bg-red-600 text-white hover:bg-red-700 active:bg-red-800",
};

const sizeStyles: Record<ButtonSize, string> = {
  sm: "px-3 py-1.5 text-sm",
  md: "px-4 py-2 text-sm",
  lg: "px-4 py-2.5 text-base",
};

export function Button({
  variant = "primary",
  size = "md",
  loading,
  disabled,
  children,
  class: className,
  ...props
}: ButtonProps) {
  return (
    <button
      class={`inline-flex items-center justify-center gap-2 rounded-lg font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-50 ${variantStyles[variant]} ${sizeStyles[size]} ${className || ""}`}
      disabled={disabled || loading}
      {...props}
    >
      {loading && <IcoSpinner class="size-4 animate-spin" />}
      {children}
    </button>
  );
}
